import { CourseIntensityDto, SaveCourseIntensityParams, School, SchoolConfig, CourseIntensitySchoolOverrideRequest, SchoolOverride } from "app/shared/model";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

export async function getCourseIntensities(): Promise<CourseIntensityDto[]> {
  const localApi = await getLocalApi();

  return await localApi.fetchList(
    apiConfig.reference_data_get_course_intensity,
    mapCourseIntensity,
    'Failed to fetch course intensities'
  );
}

export async function getCourseIntensitiesWithOverrides(): Promise<CourseIntensityDto[]> {
  const localApi = await getLocalApi();

  return await localApi.fetchList(
    apiConfig.course_intensities_get_with_overrides,
    mapCourseIntensityWithOverrides,
    'Failed to fetch course intensities with overrides'
  );
}

export function mapCourseIntensity(dto: any): CourseIntensityDto {
  return {
    id: Number(dto.id ?? dto.Id ?? 0),
    name: dto.name as string,
    active: dto.active as boolean,
    numberOfLessons: Number(dto.numberOfLessons ?? 0),
    numberOfHours: Number(dto.numberOfHours ?? 0),
    coreLessonsPerWeek: Number(dto.coreLessonsPerWeek ?? 0),
    electiveLessonsPerWeek: Number(dto.electiveLessonsPerWeek ?? 0),
    lessonLengthInMinutes: Number(dto.lessonLengthInMinutes ?? 0),
    entityUpdate: dto.entityUpdate as boolean,
  };
}

export function mapCourseIntensityWithOverrides(dto: any): CourseIntensityDto {
  return {
    id: dto.id as number,
    name: dto.name as string,
    active: dto.active as boolean,
    entityUpdate: dto.entityUpdate as boolean,
    createdBy: (dto.createdBy ?? dto.CreatedBy) as string | undefined,
    lastModifiedBy: (dto.lastModifiedBy ?? dto.LastModifiedBy) as string | undefined,
    lastModifiedDate: dto.lastModifiedDate ?? null,
    divisionId: dto.divisionId != null ? Number(dto.divisionId) : undefined,
    schoolOverrides: dto.schoolOverrides ? dto.schoolOverrides.map((override: any) => ({
      schoolId: override.schoolId as number,
      schoolName: override.schoolName as string,
      numberOfLessons: override.numberOfLessons as number,
      numberOfHours: override.numberOfHours as number,
      numberOfCores: override.numberOfCores as number,
      numberOfElectives: override.numberOfElectives as number,
      lessonLength: override.lessonLength as number,
    })) : [],
  };
}

export interface CreateCourseIntensityParams {
  id?: number;
  name: string;
  isActive: boolean;
}

export interface SaveCourseIntensityResult {
  ok: boolean;
  message?: string;
  errors?: string[];
  data?: any;
}

export async function createCourseIntensity(
  params: CreateCourseIntensityParams
): Promise<SaveCourseIntensityResult> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.course_intensities_create;

  const id = params.id !== undefined && params.id !== null ? Number(params.id) : 0;
  const payload = {
    id,
    name: params.name,
    isActive: params.isActive,
  };

  const response = await localApi.call(endpoint, 'POST', JSON.stringify(payload));
  
  if (!response) {
    return { ok: false, message: 'No response from server', errors: ['No response from server'] };
  }

  try {
    const text = await response.text().catch(() => '');
    const data = text ? JSON.parse(text) : null;
    const errors = ApiErrors.extractAllErrors(data, response, 'Failed to create course intensity');
    
    if (response.ok) {
      if (errors.length === 0) {
        return {
          ok: true,
          message: data?.message || 'Course intensity created successfully',
          data: data,
        };
      }
    }
    
    const message = ApiErrors.formatErrors(errors);
    return {
      ok: response.ok,
      message: response.ok ? (data?.message || 'Course intensity created successfully') : message,
      errors: response.ok ? undefined : errors,
      data: data,
    };
  } catch (error) {
    const errors = ApiErrors.extractAllErrors(null, response, 'Failed to create course intensity');
    if (response.ok && errors.length === 0) {
      return {
        ok: true,
        message: 'Course intensity created successfully',
      };
    }
    return {
      ok: false,
      message: ApiErrors.formatErrors(errors),
      errors: errors,
    };
  }
}

export async function saveCourseIntensity(params: SaveCourseIntensityParams) {
  const localApi = await getLocalApi();
  
  // Create
  //if (!params.id || params.id === 0) {
    const createResult = await createCourseIntensity({
      id: params.id,
      name: params.name,
      isActive: params.active,
    });
    
    if (!createResult.ok) {
      throw new Error(createResult.message || 'Failed to create course intensity');
    }
    
    return createResult;
  //}
  
  // // Update
  // return await localApi.call(
  //   apiConfig.reference_data_get_course_intensity,
  //   'PUT',
  //   params
  // );
}



export async function saveSchoolConfig(config: SchoolConfig & { intensityId: number }) {
  const localApi = await getLocalApi();
  const url = `${apiConfig.reference_data_get_course_intensity}/${config.intensityId}/configs`;
  return await localApi.call(url, config.id ? 'PUT' : 'POST', config);
}

export async function saveCourseIntensitySchoolOverride(
  overrides: CourseIntensitySchoolOverrideRequest[]
): Promise<{ ok: boolean; message: string; errors?: string[]; data?: any }> {
  const localApi = await getLocalApi();
  
  const res = await localApi.call(
    apiConfig.course_intensities_save_school_override,
    'POST',
    JSON.stringify(overrides)
  );

  if (!res) {
    return { ok: false, message: 'No response from API' };
  }

  const text = await res.text().catch(() => '');
  const data = text ? (() => {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  })() : null;

  const errors = ApiErrors.extractAllErrors(data, res, 'Failed to save course intensity school override');
  const message = res.ok
    ? (data?.message || 'School override saved successfully')
    : ApiErrors.formatErrors(errors);

  return {
    ok: res.ok,
    message,
    errors: res.ok && errors.length === 0 ? undefined : errors,
    data
  };
}
